import { useState, useRef, useCallback } from "react";
import {
    CloudRain, Gauge, Radar, Video, StickyNote, Camera, Film,
    Plus, X, Upload, Loader2, Play, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Stable per-row key — see TrialEditor.jsx hydrateKeys() for the load-time
// backfill. New rows created here mint their own `_k`.
const makeKey = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `k-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * TrialPostTest
 * -----------------------------------------------------------------------------
 * Post-test capture panel: weather → engine → autonomy → video-metrics →
 * media uploads (photos + videos to Emergent Object Storage) → test-notes
 * grid. Every field/row edit flows to `setField()` on the parent (debounced
 * PATCH). Media uploads use their own dedicated endpoints and after success
 * merge the returned item into the parent state.
 */

export default function TrialPostTest({ trial, setField }) {
    const trialId = trial.id;
    // Merge a media item into the parent trial state after upload/delete.
    const mergeMedia = useCallback((key, next) => {
        setField(key, next);
    }, [setField]);

    return (
        <div className="p-3 md:p-8 max-w-[1400px] mx-auto space-y-6">
            <WeatherSection trial={trial} setField={setField} />
            <MetricsGrid trial={trial} setField={setField} />
            <VideoMetricsSection trial={trial} setField={setField} />
            <MediaSection
                title="16 · Photos"
                icon={Camera}
                items={trial.photos || []}
                kind="photo"
                trialId={trialId}
                onChange={(next) => mergeMedia("photos", next)}
                accept="image/*"
                maxMB={100}
                emptyLabel="Drop photos here or click to upload (max 100 MB each)"
            />
            <MediaSection
                title="17 · Videos"
                icon={Film}
                items={trial.videos || []}
                kind="video"
                trialId={trialId}
                onChange={(next) => mergeMedia("videos", next)}
                accept="video/*"
                maxMB={500}
                emptyLabel="Drop videos here or click to upload (max 500 MB each)"
            />
            <TestNotesSection trial={trial} setField={setField} />
            <LegacyNotes trial={trial} setField={setField} />
        </div>
    );
}

// -----------------------------------------------------------------------------
// Shared section shell (matches the PreTest look)
// -----------------------------------------------------------------------------

function Section({ title, icon: Icon, children, right }) {
    return (
        <section className="border border-virtus-line bg-virtus-surface">
            <header className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-virtus-line">
                <div className="flex items-center gap-3">
                    {Icon && <Icon size={14} className="text-virtus-orange" />}
                    <h2 className="font-heading text-sm md:text-base font-bold tracking-tight uppercase text-white">
                        {title}
                    </h2>
                </div>
                {right}
            </header>
            <div className="p-4 md:p-5">{children}</div>
        </section>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", numeric = false }) {
    return (
        <div>
            <label className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim block mb-1">{label}</label>
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => {
                    const v = e.target.value;
                    if (numeric) {
                        onChange(v === "" ? null : Number(v));
                    } else {
                        onChange(v);
                    }
                }}
                placeholder={placeholder}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
            />
        </div>
    );
}

// -----------------------------------------------------------------------------
// 12 · Weather
// -----------------------------------------------------------------------------

function WeatherSection({ trial, setField }) {
    const w = trial.post_weather || {};
    const set = (k, v) => setField("post_weather", { ...w, [k]: v });
    return (
        <Section title="12 · Weather & Sea State" icon={CloudRain}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Conditions"     value={w.conditions}    onChange={(v) => set("conditions", v)}    placeholder="Overcast, light drizzle" />
                <Field label="Air temp (°F)"  value={w.air_temp_f}    onChange={(v) => set("air_temp_f", v)}    type="number" numeric placeholder="72" />
                <Field label="Water temp (°F)" value={w.water_temp_f} onChange={(v) => set("water_temp_f", v)}  type="number" numeric placeholder="68" />
                <Field label="Wind"           value={w.wind}          onChange={(v) => set("wind", v)}          placeholder="8kt NE, gust 12" />
                <Field label="Sea state / chop" value={w.seas}        onChange={(v) => set("seas", v)}          placeholder={'Calm · 6" chop'} />
                <Field label="Tide"           value={w.tide}          onChange={(v) => set("tide", v)}          placeholder="Slack low @ 09:14" />
            </div>
        </Section>
    );
}

// -----------------------------------------------------------------------------
// 13 · Engine  + 14 · Autonomy (side-by-side on desktop)
// -----------------------------------------------------------------------------

function MetricsGrid({ trial, setField }) {
    const e = trial.engine_metrics || {};
    const a = trial.autonomy_metrics || {};
    const setE = (k, v) => setField("engine_metrics", { ...e, [k]: v });
    const setA = (k, v) => setField("autonomy_metrics", { ...a, [k]: v });
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Section title="13 · Engine Metrics" icon={Gauge}>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="RPM (avg)"      value={e.rpm_avg}       onChange={(v) => setE("rpm_avg", v)}       type="number" numeric placeholder="3400" />
                    <Field label="Hours run"      value={e.hours_run}     onChange={(v) => setE("hours_run", v)}     type="number" numeric placeholder="1.75" />
                    <Field label="Fuel used (gal)" value={e.fuel_used_gal} onChange={(v) => setE("fuel_used_gal", v)} type="number" numeric placeholder="2.4" />
                    <Field label="Oil pressure"   value={e.oil_pressure}  onChange={(v) => setE("oil_pressure", v)}                 placeholder="60 psi" />
                </div>
            </Section>
            <Section title="14 · Autonomy Metrics" icon={Radar}>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Mission time (min)"   value={a.mission_time_min}    onChange={(v) => setA("mission_time_min", v)}    type="number" numeric placeholder="42" />
                    <Field label="Waypoints hit"        value={a.waypoints_hit}       onChange={(v) => setA("waypoints_hit", v)}       type="number" numeric placeholder="12" />
                    <Field label="Waypoints missed"    value={a.waypoints_missed}     onChange={(v) => setA("waypoints_missed", v)}    type="number" numeric placeholder="0" />
                    <Field label="Avg CTE (m)"          value={a.avg_cte_m}           onChange={(v) => setA("avg_cte_m", v)}           type="number" numeric placeholder="3.1" />
                    <Field label="Mode changes"         value={a.mode_changes}        onChange={(v) => setA("mode_changes", v)}        type="number" numeric placeholder="2" />
                </div>
            </Section>
        </div>
    );
}

// -----------------------------------------------------------------------------
// 15 · Video metrics (list of {source, duration_min, notes})
// -----------------------------------------------------------------------------

function VideoMetricsSection({ trial, setField }) {
    const rows = trial.video_metrics || [];
    const set = (i, k, v) => setField("video_metrics", rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
    const add = () => setField("video_metrics", [...rows, { _k: makeKey(), source: "", duration_min: null, notes: "" }]);
    const remove = (i) => setField("video_metrics", rows.filter((_, x) => x !== i));
    return (
        <Section title="15 · Video Metrics" icon={Video}>
            {rows.length > 0 && (
                <div className="hidden md:grid grid-cols-[1fr_120px_2fr_28px] gap-2 mb-2 font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-dim">
                    <span>Source</span><span>Duration (min)</span><span>Notes</span><span></span>
                </div>
            )}
            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={r._k || `vm-${i}`} className="grid grid-cols-1 md:grid-cols-[1fr_120px_2fr_28px] gap-2 items-center">
                        <input value={r.source || ""} onChange={(e) => set(i, "source", e.target.value)} placeholder="FPV, Cockpit, Drone"
                               className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                               data-testid={`video-metric-source-${i}`} />
                        <input value={r.duration_min ?? ""} type="number" onChange={(e) => set(i, "duration_min", e.target.value === "" ? null : Number(e.target.value))}
                               placeholder="12"
                               className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                               data-testid={`video-metric-duration-${i}`} />
                        <input value={r.notes || ""} onChange={(e) => set(i, "notes", e.target.value)} placeholder="Notes / anomalies"
                               className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                               data-testid={`video-metric-notes-${i}`} />
                        <button onClick={() => remove(i)} className="text-virtus-dim hover:text-virtus-stuck justify-self-end md:justify-self-center">
                            <X size={13} />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={add} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid="video-metric-add-btn">
                <Plus size={11} /> Add row
            </button>
        </Section>
    );
}

// -----------------------------------------------------------------------------
// 16/17 · Media upload (photos + videos)
// -----------------------------------------------------------------------------

function MediaSection({ title, icon, items, kind, trialId, onChange, accept, maxMB, emptyLabel }) {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [lightbox, setLightbox] = useState(null); // media object

    const uploadEach = useCallback(async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setUploading(true);
        const uploaded = [];
        for (const file of files) {
            if (file.size > maxMB * 1024 * 1024) {
                toast.error(`${file.name} exceeds ${maxMB}MB — skipped.`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("caption", "");
                const endpoint = `/trials/${trialId}/${kind === "photo" ? "photos" : "videos"}`;
                const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 15 * 60 * 1000 });
                uploaded.push(data);
            } catch (e) {
                console.error(`[trial-${kind}] upload failed:`, e);
                toast.error(`${file.name} — upload failed`);
            }
        }
        if (uploaded.length) {
            onChange([...(items || []), ...uploaded]);
            toast.success(`${uploaded.length} ${kind}${uploaded.length === 1 ? "" : "s"} uploaded.`);
        }
        setUploading(false);
    }, [trialId, kind, items, onChange, maxMB]);

    const onFileChange = (e) => {
        uploadEach(e.target.files);
        e.target.value = "";
    };
    const onDrop = (e) => {
        e.preventDefault();
        uploadEach(e.dataTransfer.files);
    };

    const removeItem = async (media) => {
        if (!window.confirm(`Delete ${media.filename || media.id}?`)) return;
        try {
            const path = `/trials/${trialId}/${kind === "photo" ? "photos" : "videos"}/${media.id}`;
            await api.delete(path);
            onChange((items || []).filter((m) => m.id !== media.id));
            toast.success("Removed.");
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const setCaption = async (media, caption) => {
        // Optimistic UI update
        onChange((items || []).map((m) => (m.id === media.id ? { ...m, caption } : m)));
        try {
            const path = `/trials/${trialId}/${kind === "photo" ? "photos" : "videos"}/${media.id}`;
            await api.patch(path, { caption });
        } catch (e) {
            console.error(`[trial-${kind}] caption save failed:`, e);
        }
    };

    return (
        <Section title={title} icon={icon} right={
            <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white border border-virtus-orange px-3 py-1.5 disabled:opacity-50"
                data-testid={`media-upload-btn-${kind}`}
            >
                {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                {uploading ? "UPLOADING" : "UPLOAD"}
            </button>
        }>
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple
                onChange={onFileChange}
                className="hidden"
                data-testid={`media-upload-input-${kind}`}
            />
            {items.length === 0 ? (
                <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-virtus-line hover:border-virtus-orange p-6 text-center cursor-pointer transition-colors"
                    data-testid={`media-dropzone-${kind}`}
                >
                    {kind === "photo" ? <ImageIcon size={30} className="text-virtus-dim mx-auto mb-2" strokeWidth={1} />
                                       : <Film size={30} className="text-virtus-dim mx-auto mb-2" strokeWidth={1} />}
                    <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-virtus-dim">{emptyLabel}</div>
                </div>
            ) : (
                <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="grid grid-cols-2 md:grid-cols-4 gap-3"
                >
                    {items.map((m) => (
                        <MediaTile key={m.id}
                                   media={m}
                                   trialId={trialId}
                                   kind={kind}
                                   onOpen={() => setLightbox(m)}
                                   onRemove={() => removeItem(m)}
                                   onCaption={(c) => setCaption(m, c)} />
                    ))}
                </div>
            )}
            {lightbox && (
                <Lightbox media={lightbox} kind={kind} trialId={trialId} onClose={() => setLightbox(null)} />
            )}
        </Section>
    );
}

function MediaTile({ media, trialId, kind, onOpen, onRemove, onCaption }) {
    const backendBase = process.env.REACT_APP_BACKEND_URL || "";
    const src = `${backendBase}/api/trials/${trialId}/${kind === "photo" ? "photos" : "videos"}/${media.id}/download`;
    return (
        <div className="group border border-virtus-line bg-virtus-bg overflow-hidden" data-testid={`media-tile-${media.id}`}>
            <div className="relative aspect-video bg-black cursor-pointer" onClick={onOpen}>
                {kind === "photo" ? (
                    <img src={src} alt={media.caption || media.filename} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-black">
                        <Play size={40} className="text-white/70" strokeWidth={1.5} />
                    </div>
                )}
                <button
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    className="absolute top-1 right-1 bg-black/80 text-virtus-dim hover:text-virtus-stuck p-1 border border-virtus-line opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete"
                    data-testid={`media-remove-${media.id}`}
                >
                    <X size={11} />
                </button>
            </div>
            <input
                value={media.caption || ""}
                onChange={(e) => onCaption(e.target.value)}
                placeholder="Caption…"
                className="w-full bg-transparent border-0 border-t border-virtus-line px-2 py-1.5 text-white outline-none font-mono text-[10px]"
                data-testid={`media-caption-${media.id}`}
            />
        </div>
    );
}

function Lightbox({ media, kind, trialId, onClose }) {
    const backendBase = process.env.REACT_APP_BACKEND_URL || "";
    const src = `${backendBase}/api/trials/${trialId}/${kind === "photo" ? "photos" : "videos"}/${media.id}/download`;
    return (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="media-lightbox">
            <div className="max-w-6xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
                {kind === "photo" ? (
                    <img src={src} alt={media.caption || media.filename} className="max-h-[85vh] object-contain" />
                ) : (
                    <video src={src} controls autoPlay className="max-h-[85vh]" />
                )}
                <button
                    onClick={onClose}
                    className="absolute -top-8 right-0 text-white/80 hover:text-virtus-orange font-mono text-[10px] tracking-[0.28em] uppercase"
                    data-testid="media-lightbox-close"
                >
                    CLOSE
                </button>
                {media.caption && (
                    <div className="mt-2 font-mono text-[11px] tracking-[0.24em] uppercase text-virtus-dim">
                        {media.caption}
                    </div>
                )}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// 18 · Test notes grid
// -----------------------------------------------------------------------------

function TestNotesSection({ trial, setField }) {
    const rows = trial.test_notes || [];
    const set = (i, k, v) => setField("test_notes", rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
    const add = () => setField("test_notes", [...rows, { _k: makeKey(), time: "", test: "", result: "" }]);
    const remove = (i) => setField("test_notes", rows.filter((_, x) => x !== i));
    return (
        <Section title="18 · Test Notes" icon={StickyNote}>
            {rows.length > 0 && (
                <div className="hidden md:grid grid-cols-[100px_1.4fr_2fr_28px] gap-2 mb-2 font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-dim">
                    <span>Time</span><span>Test</span><span>Result / Observation</span><span></span>
                </div>
            )}
            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={r._k || `tn-${i}`} className="grid grid-cols-1 md:grid-cols-[100px_1.4fr_2fr_28px] gap-2 items-center">
                        <input value={r.time || ""} onChange={(e) => set(i, "time", e.target.value)} placeholder="0930"
                               className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-virtus-orange outline-none font-mono text-xs"
                               data-testid={`test-note-time-${i}`} />
                        <input value={r.test || ""} onChange={(e) => set(i, "test", e.target.value)} placeholder="Test / event"
                               className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                               data-testid={`test-note-test-${i}`} />
                        <input value={r.result || ""} onChange={(e) => set(i, "result", e.target.value)} placeholder="Outcome"
                               className="bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                               data-testid={`test-note-result-${i}`} />
                        <button onClick={() => remove(i)} className="text-virtus-dim hover:text-virtus-stuck justify-self-end md:justify-self-center">
                            <X size={13} />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={add} className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange hover:text-white" data-testid="test-note-add-btn">
                <Plus size={11} /> Add note
            </button>
        </Section>
    );
}

// -----------------------------------------------------------------------------
// Legacy legacy notes area (kept so existing trials don't hide data)
// -----------------------------------------------------------------------------

function LegacyNotes({ trial, setField }) {
    return (
        <Section title="19 · Free-Form Notes & Lessons Learned" icon={StickyNote}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim block mb-1">Lessons Learned</label>
                    <textarea value={trial.lessons_learned || ""} onChange={(e) => setField("lessons_learned", e.target.value)}
                              className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm resize-y min-h-[100px]"
                              data-testid="lessons-learned-input" />
                </div>
                <div>
                    <label className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim block mb-1">Free-form Notes</label>
                    <textarea value={trial.notes || ""} onChange={(e) => setField("notes", e.target.value)}
                              className="w-full bg-virtus-bg border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm resize-y min-h-[100px]"
                              data-testid="notes-input" />
                </div>
            </div>
        </Section>
    );
}
